import React, { useState, useEffect, useRef } from "react";
import { createLeather, updateLeather, deleteLeatherItem, updateLeatherOrderAPI, toggleHideLeatherAPI } from "./fetchApi";
import toast from "react-hot-toast";
import DrawAreaModal from "./inventory/DrawAreaModal";
import LeatherFormCard from "./inventory/LeatherFormCard";
import InventoryTableCard from "./inventory/InventoryTableCard";

const Inventory = ({ leathers, fetchData, colors }) => {
    const [input, setInput] = useState({ name: "", price: "", freeMovePrice: "1600", width: "", height: "", image: null });
    const [loading, setLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [editingLeather, setEditingLeather] = useState(null);

    const [showDrawModal, setShowDrawModal] = useState(false);
    const [restrictedAreas, setRestrictedAreas] = useState([]);
    const [preSelectedAreas, setPreSelectedAreas] = useState([]);

    const [localLeathers, setLocalLeathers] = useState([]);
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    useEffect(() => {
        setLocalLeathers(leathers && leathers.length > 0 ? [...leathers] : []);
    }, [leathers]);

    const handleDragStart = (e, index) => {
        dragItem.current = index;
        e.dataTransfer.effectAllowed = "move";
        setTimeout(() => { if (e.target) e.target.style.opacity = '0.5'; }, 0);
    };

    const handleDragEnter = (e, index) => {
        e.preventDefault();
        dragOverItem.current = index;
        document.querySelectorAll('.inventory-draggable-row').forEach(row => {
            row.style.backgroundColor = 'transparent';
            row.style.borderTop = 'none';
        });
        if (e.currentTarget) {
            e.currentTarget.style.backgroundColor = 'rgba(197, 160, 117, 0.05)';
            e.currentTarget.style.borderTop = '2px solid #C5A075';
        }
    };

    const handleDragEnd = (e) => {
        if (e.target) e.target.style.opacity = '1';
        document.querySelectorAll('.inventory-draggable-row').forEach(row => {
            row.style.backgroundColor = 'transparent';
            row.style.borderTop = 'none';
        });
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) {
            handleDragEnd(e); return;
        }
        const copyListItems = [...localLeathers];
        const dragItemContent = copyListItems.splice(dragItem.current, 1)[0];
        copyListItems.splice(dragOverItem.current, 0, dragItemContent);
        dragItem.current = null; dragOverItem.current = null;
        setLocalLeathers(copyListItems);
        handleDragEnd(e);
        try {
            const res = await updateLeatherOrderAPI(copyListItems.map((item, idx) => ({ id: item._id, orderIndex: idx })));
            if (!res.success) toast.error("Có lỗi khi lưu vị trí mới!");
        } catch (error) { console.error(error); }
    };

    const compressImage = (file) => new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                let width = img.width; let height = img.height;
                if (width > height) { if (width > 1500) { height *= 1500 / width; width = 1500; } }
                else { if (height > 1500) { width *= 1500 / height; height = 1500; } }
                canvas.width = width; canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => resolve(new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" })), "image/jpeg", 0.8);
            };
        };
    });

    const handleEdit = (leather) => {
        setEditingLeather(leather);
        setInput({ name: leather.name, price: String(leather.price), freeMovePrice: String(leather.freeMovePrice || 1600), width: String(leather.width), height: String(leather.height), image: null });
        setPreviewUrl(leather.image);
        try { setRestrictedAreas(leather.restrictedAreas ? (typeof leather.restrictedAreas === 'string' ? JSON.parse(leather.restrictedAreas) : leather.restrictedAreas) : []); } catch (_) { setRestrictedAreas([]); }
        try { setPreSelectedAreas(leather.preSelectedAreas || []); } catch (_) { setPreSelectedAreas([]); }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingLeather(null);
        setInput({ name: "", price: "", freeMovePrice: "1600", width: "", height: "", image: null });
        setPreviewUrl(null); setRestrictedAreas([]); setPreSelectedAreas([]);
    };

    const handleCalculate = async (e) => {
        e.preventDefault();
        if (!input.name || !input.price || !input.freeMovePrice) return toast.error("Please fill all required fields!");
        if (!editingLeather && !input.image) return toast.error("Please upload an image!");
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("name", input.name);
            formData.append("price", input.price);
            formData.append("freeMovePrice", input.freeMovePrice);
            formData.append("width", input.width);
            formData.append("height", input.height);
            formData.append("restrictedAreas", JSON.stringify(restrictedAreas));
            formData.append("preSelectedAreas", JSON.stringify(preSelectedAreas));
            if (input.image) formData.append("image", await compressImage(input.image));

            let res;
            if (editingLeather) {
                res = await updateLeather(editingLeather._id, formData);
                if (res && res.success) { toast.success("Leather updated successfully!"); handleCancelEdit(); fetchData(); }
                else toast.error(res.error || "Update failed!");
            } else {
                res = await createLeather(formData);
                if (res && res.success) {
                    toast.success("Leather added successfully!");
                    setInput({ name: "", price: "", freeMovePrice: "1600", width: "", height: "", image: null });
                    setPreviewUrl(null); setRestrictedAreas([]); setPreSelectedAreas([]);
                    fetchData();
                } else toast.error(res.error || "An error occurred!");
            }
        } catch (error) { toast.error("Failed to process image."); } finally { setLoading(false); }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this leather?")) {
            const res = await deleteLeatherItem(id);
            if (res && res.success) { toast.success("Deleted successfully"); fetchData(); }
            else toast.error(res.error || "Failed to delete");
        }
    };

    const handleToggleHide = async (id, currentStatus) => {
        const newStatus = !currentStatus;
        setLocalLeathers(prev => prev.map(item => item._id === id ? { ...item, isHidden: newStatus } : item));
        try {
            const res = await toggleHideLeatherAPI(id, newStatus);
            if (res && res.success) { toast.success(newStatus ? "Hide leather" : "Show leather"); fetchData(); }
            else {
                setLocalLeathers(prev => prev.map(item => item._id === id ? { ...item, isHidden: currentStatus } : item));
                toast.error(res.error || "Failed to update visibility");
            }
        } catch (error) {
            setLocalLeathers(prev => prev.map(item => item._id === id ? { ...item, isHidden: currentStatus } : item));
            toast.error("Failed to update visibility");
        }
    };

    return (
        <div className="position-relative animate-fade-in">
            <DrawAreaModal
                isOpen={showDrawModal}
                onClose={() => setShowDrawModal(false)}
                previewUrl={previewUrl}
                input={input}
                restrictedAreas={restrictedAreas}
                setRestrictedAreas={setRestrictedAreas}
                preSelectedAreas={preSelectedAreas}
                setPreSelectedAreas={setPreSelectedAreas}
            />

            <div className="mb-4">
                <h3 className="fw-bold mb-1" style={{ color: colors.textMain, letterSpacing: '0.5px' }}>Leather Inventory</h3>
                <span style={{ color: colors.textMuted, fontSize: '14px' }}>Upload new leathers and manage existing stock</span>
            </div>

            <div className="row g-4">
                <div className="col-xl-4 col-lg-5">
                    <LeatherFormCard
                        input={input}
                        setInput={setInput}
                        loading={loading}
                        previewUrl={previewUrl}
                        setPreviewUrl={setPreviewUrl}
                        editingLeather={editingLeather}
                        restrictedAreas={restrictedAreas}
                        setRestrictedAreas={setRestrictedAreas}
                        preSelectedAreas={preSelectedAreas}
                        setPreSelectedAreas={setPreSelectedAreas}
                        showDrawModal={showDrawModal}
                        setShowDrawModal={setShowDrawModal}
                        colors={colors}
                        onSubmit={handleCalculate}
                        onCancel={handleCancelEdit}
                    />
                </div>
                <div className="col-xl-8 col-lg-7">
                    <InventoryTableCard
                        localLeathers={localLeathers}
                        colors={colors}
                        editingLeather={editingLeather}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleHide={handleToggleHide}
                        onDragStart={handleDragStart}
                        onDragEnter={handleDragEnter}
                        onDragEnd={handleDragEnd}
                        onDrop={handleDrop}
                    />
                </div>
            </div>
        </div>
    );
};

export default Inventory;
